/**
 * Cross-browser clipboard utility with Safari fallback.
 * Safari (especially on iOS) has limitations with navigator.clipboard.writeText,
 * so we provide a fallback using the legacy execCommand API.
 */

/**
 * Copy text to clipboard with cross-browser support.
 * Uses navigator.clipboard.writeText when available, falls back to execCommand for Safari.
 * @param text - The text to copy to clipboard
 * @returns Promise that resolves when copy succeeds, rejects on failure
 */
export async function copyToClipboard(text: string): Promise<void> {
  // Try modern Clipboard API first
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (err) {
      // Clipboard API failed (common in Safari), fall through to legacy method
      console.warn('Clipboard API failed, trying fallback:', err);
    }
  }

  // Fallback for Safari and older browsers using execCommand
  return new Promise((resolve, reject) => {
    const textArea = document.createElement('textarea');

    // Prevent scrolling to bottom of page on iOS
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';

    // Avoid iOS zoom on focus
    textArea.style.fontSize = '16px';

    textArea.value = text;
    document.body.appendChild(textArea);

    // Handle iOS specifically
    const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isiOS) {
      const range = document.createRange();
      range.selectNodeContents(textArea);

      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textArea.setSelectionRange(0, text.length);
    } else {
      textArea.focus();
      textArea.select();
    }

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        resolve();
      } else {
        reject(new Error('execCommand copy failed'));
      }
    } catch (err) {
      document.body.removeChild(textArea);
      reject(err);
    }
  });
}
